import { createContext, useContext, useRef, useState } from "react";
import Panel from "@/components/ui/Panel";
import Button from "@/components/ui/Button";
import { Field, Input, Textarea } from "@/components/ui/Field";
import { fetchWithCsrf } from "@/utils/csrf";
import {
  BLOCK_LABELS,
  BLOCK_SLOTS,
  BLOCK_TYPES,
  type EventPageBlock,
  type EventPageBlockType,
} from "@/interfaces/event_page";

// Uploading needs the event, and the field that needs it is five components
// down. Passed as context rather than drilled through every block and item.
const UploadContext = createContext<((file: File) => Promise<string>) | null>(
  null,
);

// One row of the editor. Blocks carry no id of their own -- they're a document,
// not records -- so the editor keeps a client-side key to hang React state and
// reordering off, and strips it back out before saving.
export interface BlockRow {
  key: string;
  block: EventPageBlock;
}

let nextKey = 0;
export function rowFor(block: EventPageBlock): BlockRow {
  nextKey += 1;
  return { key: `b${nextKey}`, block };
}

export function blankBlock(type: EventPageBlockType): EventPageBlock {
  switch (type) {
    case "hero":
      return { type: "hero", heading: "", subheading: "" };
    case "text":
      return { type: "text", body: "" };
    case "image":
      return { type: "image", url: "", caption: "" };
    case "schedule":
      return {
        type: "schedule",
        heading: "",
        items: [{ time: "", label: "" }],
      };
    case "faq":
      return {
        type: "faq",
        heading: "",
        items: [{ question: "", answer: "" }],
      };
    case "links":
      return { type: "links", items: [{ label: "", url: "" }] };
    case "gallery":
      return {
        type: "gallery",
        heading: "",
        items: [{ url: "", caption: "" }],
      };
    case "sponsors":
      return {
        type: "sponsors",
        heading: "",
        items: [{ name: "", url: "", logo_url: "" }],
      };
  }
}

// A photo, however the organizer has one: off their phone, or as a link they
// already have. Upload is offered first because an organizer standing in the
// venue has a camera roll and nowhere to host what is on it.
function PhotoInput({
  value,
  onChange,
  label = "Photo",
}: {
  value: string;
  onChange: (url: string) => void;
  label?: string;
}) {
  const upload = useContext(UploadContext);
  const input = useRef<HTMLInputElement>(null);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);

  async function pick(file: File | undefined) {
    if (!file || !upload) return;

    setBusy(true);
    setError(null);
    try {
      onChange(await upload(file));
    } catch (e) {
      setError(e instanceof Error ? e.message : "That photo didn't upload.");
    } finally {
      setBusy(false);
      // Cleared so picking the same file again still fires a change.
      if (input.current) input.current.value = "";
    }
  }

  return (
    <Field
      label={label}
      hint="Upload one, or paste an address starting with https://"
      error={error ?? undefined}
    >
      <div className="flex flex-col gap-2">
        <div className="flex items-center gap-3">
          {value && (
            <img
              src={value}
              alt=""
              className="border-line h-14 w-14 shrink-0 border-2 object-cover"
            />
          )}
          <input
            ref={input}
            type="file"
            accept="image/png,image/jpeg,image/webp,image/gif"
            className="hidden"
            onChange={(e) => pick(e.target.files?.[0])}
          />
          <Button
            type="button"
            variant="secondary"
            size="sm"
            disabled={busy}
            onClick={() => input.current?.click()}
          >
            {busy ? "Uploading..." : value ? "Replace photo" : "Upload a photo"}
          </Button>
          {value && (
            <Button
              type="button"
              variant="ghost"
              size="sm"
              onClick={() => onChange("")}
            >
              Remove
            </Button>
          )}
        </div>

        <Input
          value={value}
          onChange={(e) => onChange(e.target.value)}
          placeholder="https://…"
          aria-label={`${label} address`}
        />
      </div>
    </Field>
  );
}

// Adding and removing rows inside a list block (schedule, FAQ, buttons).
function ItemList<T>({
  items,
  onChange,
  blank,
  addLabel,
  render,
}: {
  items: T[];
  onChange: (next: T[]) => void;
  blank: () => T;
  addLabel: string;
  render: (item: T, set: (patch: Partial<T>) => void) => React.ReactNode;
}) {
  return (
    <div className="flex flex-col gap-3">
      {items.map((item, i) => (
        <div key={i} className="flex items-end gap-2">
          <div className="flex-1">
            {render(item, (patch) =>
              onChange(
                items.map((it, j) => (i === j ? { ...it, ...patch } : it)),
              ),
            )}
          </div>
          <Button
            type="button"
            variant="ghost"
            size="sm"
            aria-label="Remove this row"
            onClick={() => onChange(items.filter((_, j) => j !== i))}
          >
            ×
          </Button>
        </div>
      ))}

      <Button
        type="button"
        variant="secondary"
        size="sm"
        className="self-start"
        onClick={() => onChange([...items, blank()])}
      >
        {addLabel}
      </Button>
    </div>
  );
}

function BlockFields({
  block,
  onChange,
}: {
  block: EventPageBlock;
  onChange: (next: EventPageBlock) => void;
}) {
  switch (block.type) {
    case "hero":
      return (
        <div className="flex flex-col gap-4">
          <Field label="Headline">
            <Input
              value={block.heading ?? ""}
              onChange={(e) => onChange({ ...block, heading: e.target.value })}
              placeholder="Come drink boba and ship things"
            />
          </Field>
          <Field label="Under it">
            <Input
              value={block.subheading ?? ""}
              onChange={(e) =>
                onChange({ ...block, subheading: e.target.value })
              }
              placeholder="Two hours, one table, everybody welcome."
            />
          </Field>
        </div>
      );

    case "text":
      return (
        <Field
          label="Text"
          hint="Enter starts a new line. Markdown works too: **bold**, _italic_, - lists, [links](https://). Raw HTML is stripped."
        >
          <Textarea
            rows={8}
            value={block.body}
            onChange={(e) => onChange({ ...block, body: e.target.value })}
          />
        </Field>
      );

    case "image":
      return (
        <div className="flex flex-col gap-4">
          <PhotoInput
            value={block.url}
            onChange={(url) => onChange({ ...block, url })}
          />
          <Field label="Caption">
            <Input
              value={block.caption ?? ""}
              onChange={(e) => onChange({ ...block, caption: e.target.value })}
            />
          </Field>
        </div>
      );

    case "schedule":
      return (
        <div className="flex flex-col gap-4">
          <Field label="Title">
            <Input
              value={block.heading ?? ""}
              onChange={(e) => onChange({ ...block, heading: e.target.value })}
              placeholder="the-day.txt"
            />
          </Field>
          <ItemList
            items={block.items}
            onChange={(items) => onChange({ ...block, items })}
            blank={() => ({ time: "", label: "" })}
            addLabel="Add a row"
            render={(item, set) => (
              <div className="flex gap-2">
                <Input
                  value={item.time ?? ""}
                  onChange={(e) => set({ time: e.target.value })}
                  placeholder="2:00"
                  aria-label="Time"
                  className="w-24"
                />
                <Input
                  value={item.label}
                  onChange={(e) => set({ label: e.target.value })}
                  placeholder="Doors open"
                  aria-label="What happens"
                />
              </div>
            )}
          />
        </div>
      );

    case "faq":
      return (
        <div className="flex flex-col gap-4">
          <Field label="Title">
            <Input
              value={block.heading ?? ""}
              onChange={(e) => onChange({ ...block, heading: e.target.value })}
              placeholder="faq.csv"
            />
          </Field>
          <ItemList
            items={block.items}
            onChange={(items) => onChange({ ...block, items })}
            blank={() => ({ question: "", answer: "" })}
            addLabel="Add a question"
            render={(item, set) => (
              <div className="flex flex-col gap-2">
                <Input
                  value={item.question}
                  onChange={(e) => set({ question: e.target.value })}
                  placeholder="Do I need to know how to code?"
                  aria-label="Question"
                />
                <Textarea
                  rows={2}
                  value={item.answer}
                  onChange={(e) => set({ answer: e.target.value })}
                  placeholder="Nope. Genuinely."
                  aria-label="Answer"
                />
              </div>
            )}
          />
        </div>
      );

    case "gallery":
      return (
        <div className="flex flex-col gap-4">
          <Field label="Title">
            <Input
              value={block.heading ?? ""}
              onChange={(e) => onChange({ ...block, heading: e.target.value })}
              placeholder="what your event could look like..."
            />
          </Field>
          <ItemList
            items={block.items}
            onChange={(items) => onChange({ ...block, items })}
            blank={() => ({ url: "", caption: "" })}
            addLabel="Add a photo"
            render={(item, set) => (
              <div className="flex flex-col gap-2">
                <PhotoInput value={item.url} onChange={(url) => set({ url })} />
                <Input
                  value={item.caption ?? ""}
                  onChange={(e) => set({ caption: e.target.value })}
                  placeholder="Caption (optional)"
                  aria-label="Caption"
                />
              </div>
            )}
          />
        </div>
      );

    case "sponsors":
      return (
        <div className="flex flex-col gap-4">
          <Field label="Title">
            <Input
              value={block.heading ?? ""}
              onChange={(e) => onChange({ ...block, heading: e.target.value })}
              placeholder="thank you to our sponsors!"
            />
          </Field>
          <ItemList
            items={block.items}
            onChange={(items) => onChange({ ...block, items })}
            blank={() => ({ name: "", url: "", logo_url: "" })}
            addLabel="Add a sponsor"
            render={(item, set) => (
              <div className="flex flex-col gap-2">
                <Input
                  value={item.name}
                  onChange={(e) => set({ name: e.target.value })}
                  placeholder="Sunrise Tea House"
                  aria-label="Sponsor name"
                />
                <Input
                  value={item.url ?? ""}
                  onChange={(e) => set({ url: e.target.value })}
                  placeholder="https://… (optional)"
                  aria-label="Sponsor link"
                />
                <PhotoInput
                  label="Logo (optional)"
                  value={item.logo_url ?? ""}
                  onChange={(logo_url) => set({ logo_url })}
                />
              </div>
            )}
          />
        </div>
      );

    case "links":
      return (
        <ItemList
          items={block.items}
          onChange={(items) => onChange({ ...block, items })}
          blank={() => ({ label: "", url: "" })}
          addLabel="Add a button"
          render={(item, set) => (
            <div className="flex flex-col gap-2 sm:flex-row">
              <Input
                value={item.label}
                onChange={(e) => set({ label: e.target.value })}
                placeholder="Our Instagram"
                aria-label="Button text"
              />
              <Input
                value={item.url}
                onChange={(e) => set({ url: e.target.value })}
                placeholder="https://…"
                aria-label="Where it goes"
              />
            </div>
          )}
        />
      );
  }
}

export default function PageBlockEditor({
  eventId,
  rows,
  onChange,
  onActiveChange,
}: {
  eventId: number;
  rows: BlockRow[];
  onChange: (next: BlockRow[]) => void;
  // Which section the organizer is working in, so the preview can light up the
  // part of the page it belongs to. Null when focus leaves the editor.
  onActiveChange?: (index: number | null) => void;
}) {
  async function upload(file: File) {
    const body = new FormData();
    body.append("file", file);

    const res = await fetchWithCsrf(`/organize/${eventId}/page/images`, {
      method: "POST",
      body,
    });
    const json = await res.json().catch(() => ({}));
    if (!res.ok) throw new Error(json.error ?? "That photo didn't upload.");

    return json.url as string;
  }

  function replace(key: string, block: EventPageBlock) {
    onChange(rows.map((row) => (row.key === key ? { ...row, block } : row)));
  }

  function move(index: number, by: number) {
    const to = index + by;
    if (to < 0 || to >= rows.length) return;

    const next = [...rows];
    [next[index], next[to]] = [next[to], next[index]];
    onChange(next);
  }

  return (
    <UploadContext.Provider value={upload}>
      <div className="flex flex-col gap-5">
        {rows.map((row, i) => (
          <Panel
            key={row.key}
            title={BLOCK_LABELS[row.block.type]}
            // focusin/focusout rather than click: tabbing between fields should
            // move the highlight too.
            onFocusCapture={() => onActiveChange?.(i)}
            onBlurCapture={(e) => {
              if (!e.currentTarget.contains(e.relatedTarget as Node)) {
                onActiveChange?.(null);
              }
            }}
            onClose={() => onChange(rows.filter((r) => r.key !== row.key))}
            actions={
              <div className="flex gap-1">
                <Button
                  type="button"
                  variant="ghost"
                  size="sm"
                  aria-label="Move up"
                  disabled={i === 0}
                  onClick={() => move(i, -1)}
                >
                  ↑
                </Button>
                <Button
                  type="button"
                  variant="ghost"
                  size="sm"
                  aria-label="Move down"
                  disabled={i === rows.length - 1}
                  onClick={() => move(i, 1)}
                >
                  ↓
                </Button>
              </div>
            }
          >
            <div className="flex flex-col gap-3">
              {/* The public page is a layout, not a stack, so a section does not
                simply appear where it sits in this list. */}
              <p className="text-caption text-ink-soft italic">
                {BLOCK_SLOTS[row.block.type]}
              </p>

              <BlockFields
                block={row.block}
                onChange={(block) => replace(row.key, block)}
              />
            </div>
          </Panel>
        ))}

        <Panel title="add-a-section">
          <div className="flex flex-wrap gap-2">
            {BLOCK_TYPES.map((type) => (
              <Button
                key={type}
                type="button"
                variant="secondary"
                size="sm"
                onClick={() => onChange([...rows, rowFor(blankBlock(type))])}
              >
                + {BLOCK_LABELS[type]}
              </Button>
            ))}
          </div>
        </Panel>
      </div>
    </UploadContext.Provider>
  );
}
